/**
 * Voice Booking API Service for Wadakaroo
 *
 * Manages the organisation's AI phone-booking line: which pooled phone number it has
 * been assigned, and which booking attributes the bot collects.
 *
 * Phone numbers are pre-purchased into a shared Amazon Connect pool by infrastructure
 * (wadakaroo-tf) and pre-wired to one shared contact flow, so "provisioning" a workshop
 * is just claiming one of the free numbers — there is no waiting on AWS.
 *
 * Every response carries `available`, which is false when the deployment has no Amazon
 * Connect instance configured at all (e.g. local development). Treat that as "feature
 * not installed" rather than an error.
 */

import axiosInstance from './axiosInstance';

/**
 * Current voice-booking state for the organisation.
 * Always returns an object — a workshop that has never set it up gets defaults.
 *
 * @returns {Promise<{
 *   uuid: string|null,
 *   available: boolean,
 *   phoneNumber: string|null,
 *   status: 'ASSIGNED'|'RELEASED',
 *   enabled: boolean,
 *   live: boolean,
 *   askPreferredTime: boolean,
 *   askNotes: boolean,
 *   phoneBookableServiceTypes: string[],
 *   defaultServiceType: string|null,
 *   defaultDurationMinutes: number|null,
 *   serviceTypeOptions: {value: string, label: string}[]
 * }>}
 */
export const getVoiceBookingApi = async () => {
    const response = await axiosInstance.get('/organisation/voice-booking');
    return response.data;
};

/**
 * Pool numbers not yet assigned to any organisation.
 * @returns {Promise<{available: boolean, phoneNumbers: string[]}>}
 */
export const getAvailableNumbersApi = async () => {
    const response = await axiosInstance.get('/organisation/voice-booking/available');
    return response.data;
};

/**
 * Assign a pool number to this organisation and switch the bot on.
 * @param {string} phoneNumber - E.164, exactly as returned by getAvailableNumbersApi
 */
export const provisionNumberApi = async (phoneNumber) => {
    const response = await axiosInstance.post('/organisation/voice-booking/provision', {
        phoneNumber,
    });
    return response.data;
};

/**
 * Update which booking attributes the bot collects, and the defaults it applies.
 *
 * @param {Object} settings
 * @param {boolean} settings.askPreferredTime
 * @param {boolean} settings.askNotes
 * @param {string[]} settings.phoneBookableServiceTypes - empty means "all types"
 * @param {string|null} settings.defaultServiceType
 * @param {number|null} settings.defaultDurationMinutes
 */
export const updateVoiceBookingSettingsApi = async (settings) => {
    const response = await axiosInstance.put('/organisation/voice-booking/settings', settings);
    return response.data;
};

/**
 * Pause or resume the bot. The number stays assigned either way, so pausing never
 * risks losing it back to the pool.
 * @param {boolean} enabled
 */
export const setVoiceBookingEnabledApi = async (enabled) => {
    const response = await axiosInstance.put('/organisation/voice-booking/enabled', { enabled });
    return response.data;
};

/**
 * Release the number back to the shared pool. Attribute settings are retained, so
 * re-provisioning later does not silently reset how the bot behaves.
 */
export const releaseNumberApi = async () => {
    const response = await axiosInstance.delete('/organisation/voice-booking');
    return response.data;
};

const voiceBookingApi = {
    getVoiceBookingApi,
    getAvailableNumbersApi,
    provisionNumberApi,
    updateVoiceBookingSettingsApi,
    setVoiceBookingEnabledApi,
    releaseNumberApi,
};

export default voiceBookingApi;

/**
 * Everything the bot has done on this workshop's number — bookings AND missed callers.
 *
 * A rejected call used to leave nothing behind but a server log line, so a workshop could
 * lose a customer and never find out. Each item carries `recoverable`, which is true when
 * a human ringing that caller back could still win the job (closed day, service not
 * offered by phone, a date the bot could not parse). Platform faults are deliberately NOT
 * recoverable — they are real problems, but they are not lost customers to chase, and
 * mixing them in makes the list unusable.
 *
 * `spokenMessage` is exactly what the caller heard, so staff can audit the bot rather
 * than take its behaviour on trust.
 *
 * Paged and date-filtered: this table grows with CALL volume, not booking volume, so a
 * "last N" cap silently drops the older rows on a busy line.
 *
 * @param {{from?: string, to?: string, page?: number, size?: number}} [opts]
 *        from/to ISO dates (default: the last 30 days), size ≤ 100 (default 10).
 * @returns {Promise<{
 *   windowDays: number,
 *   callsInWindow: number,
 *   bookedInWindow: number,
 *   missedInWindow: number,
 *   attempts: {
 *     uuid: string,
 *     occurredAt: string,
 *     outcome: string,
 *     recoverable: boolean,
 *     callerNumber: string|null,
 *     requestedDate: string|null,
 *     spokenService: string|null,
 *     customerName: string|null,
 *     vehicleRego: string|null,
 *     spokenMessage: string|null,
 *     bookingCode: string|null
 *   }[]
 * }>}
 */
export const getVoiceActivityApi = async ({ from, to, page, size } = {}) => {
    const response = await axiosInstance.get('/organisation/voice-booking/activity', {
        params: { from, to, page, size },
    });
    return response.data;
};

/**
 * The pre-booking queue — calls waiting for a human to turn into a real booking.
 *
 * The bot no longer creates bookings. It writes down what it heard and a human converts it,
 * which is why none of these fields can be trusted as-is: names and regos are raw speech
 * recognition output. `callerNumber` is the important one — it is how the workshop rings
 * the customer back — and `referenceCode` is what the caller was told to quote.
 *
 * @param {{page?: number, size?: number}} [opts] size ≤ 100 (default 20).
 */
export const getPreBookingsApi = async ({ page, size } = {}) => {
    const response = await axiosInstance.get('/organisation/voice-booking/pre-bookings', {
        params: { page, size },
    });
    return response.data;
};

/**
 * Marks a pre-booking as turned into a real booking, clearing it from the queue.
 * Called AFTER the booking is created through the normal path, so the booking gets the
 * same validation as any other. Idempotent server-side.
 */
export const convertPreBookingApi = async (uuid, bookingUuid) => {
    const response = await axiosInstance.post(
        `/organisation/voice-booking/pre-bookings/${uuid}/convert`, { bookingUuid });
    return response.data;
};

/** Drops a pre-booking without booking it — junk transcription or wrong number. */
export const dismissPreBookingApi = async (uuid) => {
    const response = await axiosInstance.post(
        `/organisation/voice-booking/pre-bookings/${uuid}/dismiss`);
    return response.data;
};
